import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { FileText, Send, Paperclip, X, Calendar, Image as ImageIcon, Download, Trash2, Edit2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { recommendationsAPI } from '../services/api';

const RecommendationsManager = ({ appointment, onClose }) => {
  const { currentUser } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [editingRecommendation, setEditingRecommendation] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newRecommendation, setNewRecommendation] = useState({
    type: '',
    title: '',
    content: '',
    attachments: []
  });

  useEffect(() => {
    loadRecommendations();
  }, [appointment]);

  const loadRecommendations = async () => {
    if (!appointment) return;
    
    const appointmentId = appointment._id || appointment.id;
    if (!appointmentId) {
      console.error('[RecommendationsManager] No appointment ID found!', appointment);
      return;
    }
    
    setLoading(true);
    try {
      const response = await recommendationsAPI.getByAppointment(appointmentId);
      setRecommendations(response.data || []);
      console.log('[RecommendationsManager] Loaded', response.data?.length || 0, 'notes from server');
    } catch (error) {
      console.error('[RecommendationsManager] Error loading notes:', error);
      setRecommendations([]);
    }
    setLoading(false);
  };

  const saveRecommendation = async (rec) => {
    if (!appointment) return;
    
    const appointmentId = appointment._id || appointment.id;
    if (!appointmentId) {
      console.error('[RecommendationsManager] Cannot save - no appointment ID!');
      return;
    }
    
    try {
      const dataToSave = {
        ...rec,
        appointmentId: appointmentId,
        patientName: appointment.patientName,
        patientPhone: appointment.phone
      };
      
      await recommendationsAPI.create(dataToSave);
      await loadRecommendations(); // Reload from server
      return true;
    } catch (error) {
      console.error('[RecommendationsManager] Error saving note:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar la nota',
        variant: 'destructive',
      });
      return false;
    }
  };

  const deleteRecommendation = async (id) => {
    try {
      await recommendationsAPI.delete(id);
      await loadRecommendations(); // Reload from server
      toast({
        title: 'Nota eliminada',
        description: 'La nota se eliminó correctamente',
      });
    } catch (error) {
      console.error('[RecommendationsManager] Error deleting note:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la nota',
        variant: 'destructive',
      });
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: 'Los archivos deben ser menores a 10MB',
          variant: 'destructive',
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setNewRecommendation(prev => ({
          ...prev,
          attachments: [...prev.attachments, {
            name: file.name,
            type: file.type,
            data: reader.result,
            size: file.size
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => {
    setNewRecommendation(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleAddRecommendation = async () => {
    if (!newRecommendation.type || !newRecommendation.content) {
      toast({
        title: 'Error',
        description: 'Por favor completa todos los campos',
        variant: 'destructive',
      });
      return;
    }

    if (editingRecommendation) {
      // For editing, delete old and create new (simpler approach)
      await deleteRecommendation(editingRecommendation._id || editingRecommendation.id);
      const success = await saveRecommendation({
        ...newRecommendation,
        date: new Date().toISOString(),
      });
      if (success) {
        toast({
          title: 'Nota actualizada',
          description: 'Los cambios se guardaron correctamente',
        });
      }
      setEditingRecommendation(null);
    } else {
      // Create new
      const success = await saveRecommendation({
        ...newRecommendation,
        date: new Date().toISOString(),
      });
      if (success) {
        toast({
          title: 'Nota agregada',
          description: 'Se guardó en el historial del paciente',
        });
      }
    }

    setNewRecommendation({ type: '', title: '', content: '', attachments: [] });
    setShowNewForm(false);
  };

  const handleEditRecommendation = (rec) => {
    setEditingRecommendation(rec);
    setNewRecommendation({
      type: rec.type || rec.title,
      title: '',
      content: rec.content,
      attachments: rec.attachments || []
    });
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sendToWhatsApp = async (recommendation) => {
    // Prepare message
    let message = `*${recommendation.type || recommendation.title}*\n\n`;
    message += `${recommendation.content}\n\n`;
    
    if (recommendation.attachments && recommendation.attachments.length > 0) {
      message += `\n📎 Archivos adjuntos: ${recommendation.attachments.length}\n`;
      message += `(Los archivos se están descargando automáticamente en tu dispositivo)`;
    }
    
    message += `\n\n---\nEnviado desde NotiMax`;

    try {
      // Copy message to clipboard
      await navigator.clipboard.writeText(message);
      
      // Download all attachments automatically
      if (recommendation.attachments && recommendation.attachments.length > 0) {
        recommendation.attachments.forEach((file, index) => {
          setTimeout(() => {
            const link = document.createElement('a');
            link.href = file.data;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }, index * 500); // Stagger downloads by 500ms to avoid browser blocking
        });
        
        toast({
          title: '📥 Descargando archivos',
          description: `Se están descargando ${recommendation.attachments.length} archivo(s)`,
        });
      }

      // Open WhatsApp Business with pre-filled message
      const phone = appointment.phone.replace(/[^0-9]/g, '');
      const url = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;
      
      // Small delay to ensure clipboard is updated
      setTimeout(() => {
        window.open(url, '_blank');
      }, 300);

      toast({
        title: '✅ Mensaje copiado y WhatsApp Business abierto',
        description: 'Archivos descargados. Arrástralos al chat de WhatsApp',
        duration: 5000,
      });

    } catch (error) {
      // Fallback if clipboard fails
      const phone = appointment.phone.replace(/[^0-9]/g, '');
      const url = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
      
      toast({
        title: 'WhatsApp Business abierto',
        description: 'Copia el mensaje y envía los archivos manualmente',
        variant: 'default',
      });
    }
  };

  const sendAllRecommendationsToWhatsApp = async () => {
    if (recommendations.length === 0) return;

    // Prepare consolidated message
    let message = `*📋 Historial de Recomendaciones - ${appointment.patientName}*\n\n`;
    
    let totalAttachments = 0;
    recommendations.forEach((rec, index) => {
      message += `${index + 1}. *${rec.type || rec.title}*\n`;
      message += `   Fecha: ${format(new Date(rec.date), 'PPP', { locale: es })}\n\n`;
      message += `   ${rec.content}\n`;
      
      if (rec.attachments && rec.attachments.length > 0) {
        message += `   📎 ${rec.attachments.length} archivo(s) adjunto(s)\n`;
        totalAttachments += rec.attachments.length;
      }
      message += `\n---\n\n`;
    });

    message += `Total de recomendaciones: ${recommendations.length}\n`;
    message += `Total de archivos: ${totalAttachments}\n\n`;
    message += `Enviado desde NotiMax`;

    try {
      // Copy to clipboard
      await navigator.clipboard.writeText(message);

      // Download all attachments from all recommendations
      let fileIndex = 0;
      recommendations.forEach((rec) => {
        if (rec.attachments && rec.attachments.length > 0) {
          rec.attachments.forEach((file) => {
            setTimeout(() => {
              const link = document.createElement('a');
              link.href = file.data;
              link.download = file.name;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }, fileIndex * 500);
            fileIndex++;
          });
        }
      });

      if (totalAttachments > 0) {
        toast({
          title: '📥 Descargando todos los archivos',
          description: `Se están descargando ${totalAttachments} archivo(s)`,
        });
      }

      // Open WhatsApp Business
      const phone = appointment.phone.replace(/[^0-9]/g, '');
      const url = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;
      
      setTimeout(() => {
        window.open(url, '_blank');
      }, 300);

      toast({
        title: '✅ Todas las recomendaciones preparadas',
        description: `${recommendations.length} recomendaciones y ${totalAttachments} archivos listos`,
        duration: 6000,
      });

    } catch (error) {
      const phone = appointment.phone.replace(/[^0-9]/g, '');
      const url = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <ImageIcon className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  if (!appointment) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Card className="shadow-xl border-0 mb-6">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-t-lg">
            <CardTitle className="text-2xl">
              Recomendaciones - {appointment.patientName}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Show/Hide Form Button when there are existing recommendations */}
            {recommendations.length > 0 && !showNewForm && !editingRecommendation && (
              <Button
                onClick={() => setShowNewForm(true)}
                className="w-full mb-6 bg-blue-500 hover:bg-blue-600 text-white"
              >
                <FileText className="w-4 h-4 mr-2" />
                Agregar Nueva Nota
              </Button>
            )}

            {/* New Recommendation Form */}
            {(showNewForm || editingRecommendation || recommendations.length === 0) && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg mb-6">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-lg">
                    {editingRecommendation ? 'Editar Recomendación' : 'Nueva Recomendación'}
                  </h3>
                  {recommendations.length > 0 && showNewForm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowNewForm(false)}
                      className="text-gray-600 hover:text-gray-800"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancelar
                    </Button>
                  )}
                </div>
              
                <div className="space-y-2">
                <Label>Tipo de nota *</Label>
                <Input
                  value={newRecommendation.type}
                  onChange={(e) => setNewRecommendation(prev => ({ ...prev, type: e.target.value }))}
                  placeholder="Ej: Tratamiento, Dieta, Seguimiento..."
                />
              </div>

              <div className="space-y-2">
                <Label>Descripción detallada *</Label>
                <Textarea
                  value={newRecommendation.content}
                  onChange={(e) => setNewRecommendation(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Describe la recomendación con detalle..."
                  rows={6}
                  className="resize-none"
                />
              </div>

              <div className="space-y-2">
                <Label>Adjuntar archivos o imágenes</Label>
                <div>
                  <input
                    type="file"
                    id="file-upload"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Label htmlFor="file-upload">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('file-upload').click()}
                    >
                      <Paperclip className="w-4 h-4 mr-2" />
                      Adjuntar archivo
                    </Button>
                  </Label>
                </div>
                
                {newRecommendation.attachments.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {newRecommendation.attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                        <div className="flex items-center space-x-2">
                          {getFileIcon(file.type)}
                          <span className="text-sm">{file.name}</span>
                          <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAttachment(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                </div>

                <div className="flex space-x-2">
                  <Button
                    onClick={handleAddRecommendation}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-green-500 text-white"
                  >
                    {editingRecommendation ? 'Actualizar' : 'Guardar'} Recomendación
                  </Button>
                  {editingRecommendation && (
                    <Button
                      type="button"
                      onClick={() => {
                        setEditingRecommendation(null);
                        setNewRecommendation({ type: '', title: '', content: '', attachments: [] });
                      }}
                      variant="outline"
                    >
                      Cancelar
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Recommendations History */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg">Historial de Recomendaciones</h3>
                {recommendations.length > 0 && (
                  <Button
                    onClick={() => sendAllRecommendationsToWhatsApp()}
                    className="bg-green-500 hover:bg-green-600 text-white"
                    size="sm"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Enviar todas por WhatsApp
                  </Button>
                )}
              </div>
              
              {recommendations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No hay recomendaciones registradas</p>
                </div>
              ) : (
                recommendations.map((rec) => (
                  <Card key={rec.id} className="border-l-4 border-l-green-500">
                    <CardContent className="pt-4">
                      <div className="mb-3">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg">{rec.type || rec.title}</h4>
                            <div className="flex items-center flex-wrap gap-2 mt-1">
                              <span className="text-xs text-gray-500">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                {format(new Date(rec.date), 'PPP', { locale: es })}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <button
                            onClick={() => sendToWhatsApp(rec)}
                            className="bg-green-500 hover:bg-green-600 text-white text-xs px-4 py-2 rounded-md flex items-center gap-1.5 min-w-max"
                            title="Enviar por WhatsApp"
                          >
                            <Send className="w-3 h-3 flex-shrink-0" />
                            <span className="whitespace-nowrap">WhatsApp</span>
                          </button>
                          <button
                            onClick={() => handleEditRecommendation(rec)}
                            className="border border-gray-300 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300 text-gray-700 px-3 py-2 rounded-md flex items-center justify-center"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteRecommendation(rec._id || rec.id)}
                            className="border border-gray-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300 text-gray-700 px-3 py-2 rounded-md flex items-center justify-center"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-700 whitespace-pre-wrap mb-3">
                        {rec.content}
                      </p>

                      {rec.attachments && rec.attachments.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs font-semibold text-gray-600">Archivos adjuntos:</p>
                          {rec.attachments.map((file, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <div className="flex items-center space-x-2">
                                {getFileIcon(file.type)}
                                <span className="text-sm">{file.name}</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = file.data;
                                  link.download = file.name;
                                  link.click();
                                }}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            <div className="mt-6">
              <Button
                onClick={onClose}
                variant="outline"
                className="w-full"
              >
                Cerrar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RecommendationsManager;
